import React, { useState, useEffect, useCallback } from 'react';
import { apiGet } from '@/lib/api/client';
import { ACTION_LABELS, describeAuditEntry } from '@/common/utils/audit-labels';

interface AuditEntry {
  id: string;
  action: string;
  actor_id: string;
  target_type: string | null;
  target_id: string | null;
  details: Record<string, string> | null;
  created_at: number;
}

interface Props {
  workspaceId: string;
}

const ACTION_OPTIONS = Object.keys(ACTION_LABELS);
const PAGE_SIZE = 50;

export function ActivitySection({ workspaceId }: Props) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [actionFilter, setActionFilter] = useState<string>('');

  const fetchEntries = useCallback(async (before?: number, append = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (before) params.set('before', String(before));
      const res = await apiGet<{ entries: AuditEntry[] }>(`/workspaces/${workspaceId}/audit-log?${params}`);
      const newEntries = res.data.entries ?? [];
      if (append) {
        setEntries(prev => [...prev, ...newEntries]);
      } else {
        setEntries(newEntries);
      }
      setHasMore(newEntries.length >= PAGE_SIZE);
    } catch {
      if (!append) setEntries([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const filtered = actionFilter
    ? entries.filter(e => e.action === actionFilter)
    : entries;

  return (
    <div>
      <div className="ws-settings-section-header">
        <h2>Activity</h2>
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="ws-settings-select"
        >
          <option value="">All actions</option>
          {ACTION_OPTIONS.map(action => (
            <option key={action} value={action}>{ACTION_LABELS[action]}</option>
          ))}
        </select>
      </div>

      {loading && entries.length === 0 ? (
        <p className="ws-settings-muted">Loading activity...</p>
      ) : filtered.length === 0 ? (
        <p className="ws-settings-muted">{actionFilter ? 'No matching activity.' : 'No activity recorded yet.'}</p>
      ) : (
        <div className="ws-settings-table">
          {filtered.map(entry => {
            const date = new Date(entry.created_at);
            const timeStr = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
              ' at ' + date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
            return (
              <div key={entry.id} className="ws-settings-row">
                <div className="ws-settings-row-info">
                  <span className="ws-settings-row-name">{describeAuditEntry(entry.action, entry.details)}</span>
                </div>
                <span className="ws-settings-row-time">{timeStr}</span>
              </div>
            );
          })}
        </div>
      )}

      {hasMore && !actionFilter && (
        <button
          onClick={() => {
            const last = entries[entries.length - 1];
            if (last) fetchEntries(last.created_at, true);
          }}
          disabled={loading}
          className="ws-settings-btn-secondary ws-settings-load-more"
        >
          {loading ? 'Loading...' : 'Load more'}
        </button>
      )}
    </div>
  );
}
